// Transforme wedding customiser: 3D Miracle Latex mattress with the chosen symbol and names.
// Textures are flattened from Getha's launch render (getha-wedding-customiser/tools/build_assets.py).
// The panel sits on the foot end, as in the render. King size, metres. One script serves both
// products: Miracle Latex (tight top, 28 cm) and Miracle Primefibre (Euro top, 33 cm).
import * as THREE from './wedding-three.module.min.js';
import { OrbitControls } from './wedding-orbit-controls.js';

const root = document.querySelector('[data-wedding-customiser]');
const CFG = JSON.parse(root.querySelector('[data-wc-config]').textContent);
const META = CFG.meta, A = CFG.assets, MODEL = CFG.model === 'prime' ? 'prime' : 'latex';
const [W, D] = META.king;
const BODY_H = MODEL === 'prime' ? META.heights.prime_body : META.heights.latex;
const H = BODY_H + (MODEL === 'prime' ? META.heights.euro : 0);       // full mattress height
const BAND = META.bands[MODEL];
const R = 0.035, PIPE_R = 0.0075;             // plan corner radius, piping cord
// Getha stitches names in Brush Script MT. Devices that have it (Mac, Windows) preview in it;
// others fall back to Damion, the closest free match.
const NAME_FONT = '"Brush Script MT","Brush Script Std",Damion,cursive';
const MAX = 18;
const $ = (s) => root.querySelector(s);

// ---------- product data ----------
// Symbol code from the option value ("囍 XI 1" -> XI1); series from the SKU's second part:
// TMWL / TMPWL = With Love, TMWB / TMPWB = With Blessing
const code = (v) => v.replace(/[^A-Z0-9]/g, '');
const variants = CFG.variants.map((v) => ({ ...v, size: v.options[0], sym: code(v.options[1]), group: /WB$/.test((v.sku || '').split('-')[1] || '') ? 'bless' : 'love' }));
const SIZES = [...new Set(variants.map((v) => v.size))];
const SYMBOLS = [...new Map(variants.map((v) => [v.sym, { id: v.sym, label: v.options[1], group: v.group }])).values()];
const start = variants.find((v) => v.id === CFG.selected) || variants[0];
// One line of names (max 18) at the top or bottom of the panel, or two lines: names at the top
// and a date or message at the bottom (max 18 each); in dark brown, navy blue or gold
const COLOURS = CFG.colours;
// The page opens on names and date, so the panel shows a demo until the customer types
// custom: the customer's own logo replaces the symbol (Getha's custom embroidery, priced in Zepto)
const state = { size: start.size, sym: start.sym, place: 'both', name: '', line2: '', colour: COLOURS[0], custom: false, logo: null };
const EXAMPLE = 'Jonathan & Jessica', EXAMPLE_DATE = '14.10.2026';
// A custom logo orders the first With Love variant; the order says the logo replaces its symbol
const CUSTOM_SYM = (SYMBOLS.find((s) => s.group === 'love') || SYMBOLS[0]).id;
const current = () => variants.find((v) => v.size === state.size && v.sym === (state.custom ? CUSTOM_SYM : state.sym));
const tidy = (s) => s.trim().replace(/\s+/g, ' ');

function money(cents) {
  const n = (cents / 100).toFixed(2), [i, d] = n.split('.');
  const grp = (s, sep) => s.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
  return CFG.moneyFormat.replace(/<[^>]*>/g, '').replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => ({
    amount: `${grp(i, ',')}.${d}`, amount_no_decimals: grp(i, ','),
    amount_with_comma_separator: `${grp(i, '.')},${d}`, amount_no_decimals_with_comma_separator: grp(i, '.'),
  }[k] ?? `${grp(i, ',')}.${d}`));
}

// ---------- renderer ----------
const stage = $('.wc-stage');
const canvasEl = $('.wc-canvas');
const renderer = new THREE.WebGLRenderer({ canvas: canvasEl, antialias: true, alpha: true });
renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.setClearColor(0x000000, 0);
const maxAniso = renderer.capabilities.getMaxAnisotropy();

const scene = new THREE.Scene();
const camera = new THREE.PerspectiveCamera(30, 1, 0.05, 50);
const controls = new OrbitControls(camera, canvasEl);
Object.assign(controls, { enableDamping: true, enablePan: false, enableZoom: true, minDistance: 0.4, maxDistance: 9, maxPolarAngle: Math.PI * 0.47, autoRotate: true, autoRotateSpeed: 0.5 });

// The textures carry the render's own light, so scene light only adds a little shape
scene.add(new THREE.HemisphereLight(0xffffff, 0xd9cfc4, 0.55));
const key = new THREE.DirectionalLight(0xffffff, 0.5); key.position.set(-2, 3, 4); scene.add(key);

const loader = new THREE.TextureLoader();
function tex(url, { flipX = false } = {}) {
  const t = loader.load(url); t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = maxAniso;
  if (flipX) { t.wrapS = THREE.RepeatWrapping; t.repeat.x = -1; t.offset.x = 1; }
  return t;
}
function baked(t) {
  return new THREE.MeshStandardMaterial({ map: t, emissive: 0xffffff, emissiveMap: t, emissiveIntensity: 0.6, roughness: 1, metalness: 0 });
}
function canvas(w, h) { const c = document.createElement('canvas'); c.width = w; c.height = h; return c; }

// ---------- geometry ----------
// Side wall around a rounded rectangle. Four groups (front, right, back, left), each running
// corner to corner, so a flattened face texture lands on it 1:1.
function sideWall(w, d, h, r, seg = 10) {
  const cx = w / 2 - r, cz = d / 2 - r, deg = Math.PI / 180;
  const pos = [], nor = [], uv = [], idx = [];
  const g = new THREE.BufferGeometry();
  for (let k = 0; k < 4; k++) {
    const ts = (135 - 90 * k) * deg, pts = [];
    const ctr = (t) => [Math.sign(Math.round(Math.cos(t) * 1e6)) * cx, Math.sign(Math.round(Math.sin(t) * 1e6)) * cz];
    const c1 = ctr(ts), c2 = ctr(ts - 90 * deg);
    for (let i = 0; i <= seg; i++) { const t = ts - (45 * deg) * i / seg; pts.push([c1[0] + r * Math.cos(t), c1[1] + r * Math.sin(t), t]); }
    for (let i = 0; i <= seg; i++) { const t = ts - 45 * deg - (45 * deg) * i / seg; pts.push([c2[0] + r * Math.cos(t), c2[1] + r * Math.sin(t), t]); }
    const len = [0];
    for (let i = 1; i < pts.length; i++) len.push(len[i - 1] + Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]));
    const total = len[len.length - 1], base = pos.length / 3, start = idx.length;
    pts.forEach(([x, z, t], i) => {
      for (const y of [0, h]) { pos.push(x, y, z); nor.push(Math.cos(t), 0, Math.sin(t)); uv.push(len[i] / total, y / h); }
    });
    for (let i = 0; i < pts.length - 1; i++) {
      const b0 = base + i * 2, t0 = b0 + 1, b1 = b0 + 2, t1 = b0 + 3;
      idx.push(b0, b1, t1, b0, t1, t0);
    }
    g.addGroup(start, idx.length - start, k);
  }
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(idx);
  return g;
}
function cap(w, d, r, up) {
  const s = new THREE.Shape(), x = w / 2, y = d / 2;
  s.moveTo(-x + r, -y); s.lineTo(x - r, -y); s.absarc(x - r, -y + r, r, -Math.PI / 2, 0); s.lineTo(x, y - r);
  s.absarc(x - r, y - r, r, 0, Math.PI / 2); s.lineTo(-x + r, y); s.absarc(-x + r, y - r, r, Math.PI / 2, Math.PI);
  s.lineTo(-x, -y + r); s.absarc(-x + r, -y + r, r, Math.PI, Math.PI * 1.5);
  const g = new THREE.ShapeGeometry(s, 8);
  g.rotateX(up ? -Math.PI / 2 : Math.PI / 2);
  const p = g.attributes.position, uv = g.attributes.uv;
  for (let i = 0; i < p.count; i++) uv.setXY(i, (p.getX(i) + w / 2) / w, (d / 2 - p.getZ(i)) / d);
  return g;
}
function piping(w, d, r, y, mat) {
  const cx = w / 2 - r, cz = d / 2 - r, path = new THREE.CurvePath();
  const P = (x, z) => new THREE.Vector3(x, y, z);
  const arc = (x, z, a0) => new THREE.CatmullRomCurve3(Array.from({ length: 9 }, (_, i) => { const a = a0 + i / 8 * Math.PI / 2; return P(x + Math.cos(a) * r, z + Math.sin(a) * r); }));
  path.add(arc(cx, cz, 0)); path.add(new THREE.LineCurve3(P(cx, d / 2), P(-cx, d / 2)));
  path.add(arc(-cx, cz, Math.PI / 2)); path.add(new THREE.LineCurve3(P(-w / 2, cz), P(-w / 2, -cz)));
  path.add(arc(-cx, -cz, Math.PI)); path.add(new THREE.LineCurve3(P(-cx, -d / 2), P(cx, -d / 2)));
  path.add(arc(cx, -cz, Math.PI * 1.5)); path.add(new THREE.LineCurve3(P(w / 2, -cz), P(w / 2, cz)));
  return new THREE.Mesh(new THREE.TubeGeometry(path, 900, PIPE_R, 10, true), mat);
}
// u along the front wall -> world x
function frontX(w, r, u) {
  const arc = Math.PI * r / 4, total = 2 * arc + (w - 2 * r);
  return -w / 2 + r + (u * total - arc);
}

// Braided cord matched to the render's piping
function braidTexture() {
  const c = canvas(64, 32), x = c.getContext('2d');
  x.fillStyle = '#e9d7b8'; x.fillRect(0, 0, 64, 32); x.lineWidth = 3;
  for (let i = -64; i < 128; i += 10) {
    x.strokeStyle = 'rgba(255,249,236,0.85)'; x.beginPath(); x.moveTo(i, 0); x.lineTo(i + 32, 32); x.stroke();
    x.strokeStyle = 'rgba(178,140,82,0.5)'; x.beginPath(); x.moveTo(i + 32, 0); x.lineTo(i, 32); x.stroke();
  }
  const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace;
  t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(1100, 1);
  return t;
}
const pipeMat = (() => { const t = braidTexture(); return new THREE.MeshStandardMaterial({ map: t, emissive: 0xffffff, emissiveMap: t, emissiveIntensity: 0.55, roughness: 0.7 }); })();
const baseMat = new THREE.MeshStandardMaterial({ color: 0x5a1520, roughness: 1 });

// Soft floor shadow
(function floorShadow() {
  const S = 1024, M = 3.6, ppm = S / M, c = canvas(S, S), x = c.getContext('2d');
  const blob = (w, h, blur, a) => {
    x.save(); x.shadowColor = `rgba(45,20,15,${a})`; x.shadowBlur = blur; x.shadowOffsetX = S * 2; x.fillStyle = '#000';
    x.beginPath(); x.roundRect(S / 2 - w / 2 - S * 2, S / 2 - h / 2, w, h, 24); x.fill(); x.restore();
  };
  blob(W * ppm * 1.03, D * ppm * 1.03, 70, 0.4); blob(W * ppm * 0.99, D * ppm * 0.99, 14, 0.5);
  const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace;
  const m = new THREE.Mesh(new THREE.PlaneGeometry(M, M), new THREE.MeshBasicMaterial({ map: t, transparent: true, depthWrite: false }));
  m.rotation.x = -Math.PI / 2; m.position.y = 0.001; scene.add(m);
})();

// ---------- panel decal: render panel + photo symbol + names ----------
const symImg = {};
function img(url) { return new Promise((res, rej) => { const i = new Image(); i.crossOrigin = 'anonymous'; i.onload = () => res(i); i.onerror = rej; i.src = url; }); }
const bandBg = img(A.band[MODEL]);
function symbol(id) { return (symImg[id] ||= img(A.sym[id])); }

let decal = null;
function makeDecal(h) {
  const b = BAND;
  const [cw, ch] = b.decalPx, c = canvas(cw, ch);
  const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = maxAniso;
  const x0 = frontX(W, R, b.x0), x1 = frontX(W, R, b.x1);
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(x1 - x0, h), new THREE.MeshStandardMaterial({ map: t, emissive: 0xffffff, emissiveMap: t, emissiveIntensity: 0.6, roughness: 1, polygonOffset: true, polygonOffsetFactor: -2 }));
  mesh.position.set((x0 + x1) / 2, h / 2, D / 2 + 0.0008);
  decal = { c, t, mesh, x0, x1, h };
  drawDecal();
  return mesh;
}

// One line of embroidered script, as large as the box allows
function drawThread(ctx, text, cx, cy, boxW, boxH, colour, alpha) {
  let fs = boxH * 0.9;
  ctx.font = `400 ${fs}px ${NAME_FONT}`; ctx.letterSpacing = '0px';
  const w = ctx.measureText(text).width;
  if (w > boxW) { fs *= boxW / w; ctx.font = `400 ${fs}px ${NAME_FONT}`; }
  ctx.save(); ctx.globalAlpha = alpha;
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.fillStyle = 'rgba(40,25,15,0.3)'; ctx.fillText(text, cx + fs * 0.02, cy + fs * 0.03);   // stitch shadow
  ctx.fillStyle = colour; ctx.fillText(text, cx, cy);
  ctx.save(); ctx.globalAlpha *= 0.18; ctx.fillStyle = '#fff'; ctx.fillText(text, cx - fs * 0.01, cy - fs * 0.015); ctx.restore();   // thread sheen
  ctx.restore();
}
let drawSeq = 0;
async function drawDecal() {
  if (!decal) return;
  const seq = ++drawSeq, { c, t } = decal, b = BAND, s = b.sym, ctx = c.getContext('2d');
  const [bg, sym] = await Promise.all([bandBg, state.custom ? null : symbol(state.sym)]);
  if (seq !== drawSeq) return;
  ctx.clearRect(0, 0, c.width, c.height);
  ctx.drawImage(bg, 0, 0, c.width, c.height);
  const sx = s.x0 * c.width, sy = s.y0 * c.height, sw = (s.x1 - s.x0) * c.width, sh = (s.y1 - s.y0) * c.height;
  if (!state.custom) ctx.drawImage(sym, sx, sy, sw, sh);
  else {
    // A custom logo takes the whole cream panel, from the top text line to the bottom one
    const fx = sx, fw = sw, fy = b.textBoxes.above.y0 * c.height, fh = b.textBoxes.below.y1 * c.height - fy;
    const lg = state.logo;
    if (lg && !lg.pdf) {
      const lw = lg.naturalWidth || fw, lh = lg.naturalHeight || fh;
      const k = Math.min(fw / lw, fh / lh), w = lw * k, h = lh * k;
      ctx.drawImage(lg, fx + (fw - w) / 2, fy + (fh - h) / 2, w, h);
    } else {
      // No file yet, or a PDF we cannot draw here: a dashed box the size of the logo area
      ctx.save(); ctx.setLineDash([fw * 0.05, fw * 0.035]); ctx.lineWidth = fw * 0.012; ctx.strokeStyle = 'rgba(120,90,70,0.55)';
      ctx.strokeRect(fx + fw * 0.04, fy + fw * 0.04, fw * 0.92, fh - fw * 0.08);
      ctx.fillStyle = 'rgba(110,80,60,0.75)'; ctx.font = `600 ${Math.round(fw * 0.1)}px sans-serif`; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText('Your logo', fx + fw / 2, fy + fh / 2 - (lg ? fw * 0.07 : 0));
      if (lg) { ctx.font = `500 ${Math.round(fw * 0.06)}px sans-serif`; ctx.fillText('PDF received', fx + fw / 2, fy + fh / 2 + fw * 0.08); }
      ctx.restore();
    }
  }
  // The names and date sit on the cream panel, above or below the symbol. Until the customer
  // types, demo names and a demo date fill each line.
  for (const l of slots()) {
    const z = b.textBoxes[l.zone];
    drawThread(ctx, l.text || l.example, c.width * (z.x0 + z.x1) / 2, c.height * (z.y0 + z.y1) / 2, c.width * (z.x1 - z.x0), c.height * (z.y1 - z.y0), state.colour.hex, 1);
  }
  t.needsUpdate = true;
  drawProof();
}

// ---------- visualise your design: the panel flat, as it will be stitched ----------
const PLACE_NAME = { above: 'Top', below: 'Bottom' };
function drawProof() {
  const p = $('[data-wc-proof]');
  if (!p || !decal) return;
  p.getContext('2d').drawImage(decal.c, 0, 0, p.width, p.height);
  const list = $('[data-wc-proof-list]'), sym = SYMBOLS.find((s) => s.id === state.sym);
  const rows = [['Symbol', state.custom ? `Your own logo${state.logo ? ` (${state.logo.fileName})` : ''}` : sym?.label || '']];
  for (const l of slots()) rows.push([PLACE_NAME[l.zone], l.text || `${l.example} (example)`]);
  if (slots().length) rows.push(['Thread', state.colour.label]);
  list.replaceChildren(...rows.flatMap(([k, v]) => { const dt = document.createElement('dt'), dd = document.createElement('dd'); dt.textContent = k; dd.textContent = v; return [dt, dd]; }));
}

// ---------- mattress ----------
// One wall of side faces between two piping cords. Primefibre stacks its Euro top on the body.
function block(h, y, kind) {
  const f = A.faces[kind], g = new THREE.Group();
  const wall = new THREE.Mesh(sideWall(W, D, h, R), [
    baked(tex(f.front)), baked(tex(f.right)), baked(tex(f.back)), baked(tex(f.left, { flipX: true })),
  ]);
  wall.position.y = y;
  g.add(wall, piping(W, D, R, y + h, pipeMat), piping(W, D, R, y, pipeMat));
  return g;
}
function buildMattress() {
  const g = new THREE.Group();
  g.add(block(BODY_H, 0, MODEL));
  if (MODEL === 'prime') g.add(block(META.heights.euro, BODY_H, 'euro'));
  const top = new THREE.Mesh(cap(W, D, R, true), baked(tex(A.top))); top.position.y = H; g.add(top);
  const bottom = new THREE.Mesh(cap(W, D, R, false), baseMat); bottom.position.y = 0.0005; g.add(bottom);
  g.add(makeDecal(BODY_H));
  scene.add(g);
}

// ---------- camera ----------
let fly = null;
function viewFor(view) {
  const aspect = camera.aspect, t = Math.tan(THREE.MathUtils.degToRad(camera.fov / 2));
  if (view === 'close') {
    const bx = (decal.x0 + decal.x1) / 2;
    const tgt = new THREE.Vector3(bx, decal.h / 2, D / 2);
    const span = 0.42, dist = Math.max(0.75, span / (2 * t * Math.min(aspect, 1.4)));
    return { pos: tgt.clone().add(new THREE.Vector3(0.32, 0.22, 1).normalize().multiplyScalar(dist)), tgt };
  }
  // The render's angle: looking at the foot end from front-right and above
  const hHalf = Math.atan(t * aspect), radius = Math.hypot(W / 2, D / 2, H / 2);
  const dist = radius * 1.15 / Math.sin(Math.min(THREE.MathUtils.degToRad(camera.fov / 2), hHalf));
  const tgt = new THREE.Vector3(0, H * 0.4, 0);
  return { pos: new THREE.Vector3(0.5, 0.42, 0.76).normalize().multiplyScalar(dist).add(tgt), tgt };
}
function flyTo(view) {
  if (stage.classList.contains('wc-photo-on')) showStage(null);   // any change brings the 3D back
  root.querySelectorAll('.wc-views button').forEach((b) => b.classList.toggle('wc-on', b.dataset.view === view));
  fly = viewFor(view); controls.autoRotate = view === 'full';
}
controls.addEventListener('start', () => { fly = null; controls.autoRotate = false; });
// The stage shows the 3D mattress (no button) or the photo of the thumbnail picked
function showStage(btn) {
  const url = btn?.dataset.wcThumb || '', im = $('[data-wc-photo-img]');
  stage.classList.toggle('wc-photo-on', !!url); $('[data-wc-photo]').hidden = !url;
  if (url) { im.src = url; im.alt = btn.dataset.wcAlt; im.style.objectPosition = btn.dataset.wcPos || ''; }
  root.querySelectorAll('[data-wc-thumb]').forEach((b) => {
    const on = url ? b === btn : !b.dataset.wcThumb;
    b.classList.toggle('wc-on', on); b.setAttribute('aria-pressed', on);
  });
}

function resize() {
  const { clientWidth: w, clientHeight: h } = stage;
  if (!w || !h) return;
  renderer.setSize(w, h, false); camera.aspect = w / h; camera.updateProjectionMatrix();
}
new ResizeObserver(resize).observe(stage);
let visible = true;
new IntersectionObserver(([e]) => { visible = e.isIntersecting; }).observe(stage);
function tick() {
  requestAnimationFrame(tick);
  if (!visible) return;
  if (fly) {
    camera.position.lerp(fly.pos, 0.07); controls.target.lerp(fly.tgt, 0.07);
    if (camera.position.distanceTo(fly.pos) < 0.002 && controls.target.distanceTo(fly.tgt) < 0.002) fly = null;
  }
  controls.update(); renderer.render(scene, camera);
}

// ---------- form ----------
const form = $('.wc-form'), idInput = $('[data-wc-id]'), cta = $('.wc-cta');
const props = { place: $('[data-wc-prop="place"]'), name: $('[data-wc-prop="name"]'), line2: $('[data-wc-prop="line2"]'), colour: $('[data-wc-prop="colour"]') };
const PLACE_LABEL = { top: 'Name at the top', bottom: 'Name at the bottom', both: 'Name at the top, second line at the bottom' };
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

// What each placement stitches, top to bottom: the panel zone, the typed text and its example
function slots() {
  if (state.custom) return [];   // a custom logo fills the whole panel
  const name = { text: tidy(state.name), example: EXAMPLE };
  if (state.place === 'top') return [{ zone: 'above', ...name }];
  if (state.place === 'bottom') return [{ zone: 'below', ...name }];
  if (state.place === 'both') return [{ zone: 'above', ...name }, { zone: 'below', text: tidy(state.line2), example: EXAMPLE_DATE }];
  return [];
}
// The lines that will be stitched. The names come first; a second line only goes with names.
function lines() {
  const ls = slots();
  return ls[0]?.text ? ls.filter((l) => l.text) : [];
}

// ---------- Zepto bridge ----------
// Getha charges for names through the Zepto Product Personalizer. The names are Zepto's first
// line (top or bottom); the date or message is its second line, stitched at the bottom. Zepto attaches its
// fields to this form. They stay hidden and are filled from our inputs, so Zepto still prices
// the lines and orders read exactly as they do today.
const zepto = () => form.querySelector('.product-personalizer');
const zField = (re, sel) => [...(zepto()?.querySelectorAll(sel) || [])].find((i) => re.test(i.name));
async function zWait(re, sel) {
  for (let i = 0; i < 40; i++) { const f = zField(re, sel); if (f) return f; await wait(50); }
  return null;
}
async function zCheck(re, on) {
  const c = on ? await zWait(re, 'input[type=checkbox]') : zField(re, 'input[type=checkbox]');
  if (c && c.checked !== on) { c.click(); await wait(80); }
}
function zSet(el, value) {
  if (!el || el.value === value) return;
  el.value = value;
  for (const ev of ['input', 'keyup', 'change']) el.dispatchEvent(new Event(ev, { bubbles: true }));
}
// logo: the Zepto checkbox that charges custom embroidery, e.g. "Custom Embroidery (RM888)"
const ZEPTO = { add: /\[Add Embroidery\]$/, pos: /Position/, first: /First Embroidery Line \(/, more: /\[Add More Embroidery\]$/, second: /Second Embroidery Line/, colour: /\[Color Choices\]$/, logo: /\[Custom Embroidery/i };
// Thread colour: click Zepto's own swatch so its field and preview follow
async function zColour(name) {
  const f = zField(ZEPTO.colour, 'input');
  if (!f || f.value === name) return;
  const sw = [...(zepto()?.querySelectorAll('.pplr-swatch-element') || [])].find((e) => e.dataset.name === name && e.closest('.pplr-wrapper')?.contains(f));
  if (sw) { sw.click(); await wait(80); }
}
const wantsLogo = () => state.custom && !!state.logo;
async function zApply() {
  await zCheck(ZEPTO.logo, wantsLogo());
  const ls = lines();
  if (!ls.length) {
    zSet(zField(ZEPTO.first, 'input'), ''); zSet(zField(ZEPTO.second, 'input'), '');
    await zCheck(ZEPTO.more, false); await zCheck(ZEPTO.add, false);
  } else {
    await zCheck(ZEPTO.add, true);
    await zColour(state.colour.zepto);
    zSet(await zWait(ZEPTO.pos, 'select'), ls[0].zone === 'above' ? 'Top' : 'Bottom');
    zSet(await zWait(ZEPTO.first, 'input'), ls[0].text);
    if (ls[1]) { await zCheck(ZEPTO.more, true); zSet(await zWait(ZEPTO.second, 'input'), ls[1].text); }
    else { zSet(zField(ZEPTO.second, 'input'), ''); await zCheck(ZEPTO.more, false); }
  }
}
// If Zepto's fields change (renamed in its admin), names or the logo would go on the order
// unpriced: check the lines, the colour and the logo charge all landed
function zLanded() {
  const want = lines(), col = zField(ZEPTO.colour, 'input');
  const landed = (re, l) => { const f = zField(re, 'input'); return f && !f.disabled && f.value === l.text; };
  const names = !want.length || (landed(ZEPTO.first, want[0]) && (!want[1] || landed(ZEPTO.second, want[1])) && col && col.value === state.colour.zepto);
  const logo = !wantsLogo() || !!zField(ZEPTO.logo, 'input[type=checkbox]')?.checked;
  return names && logo;
}
let zQueue = Promise.resolve(), zPending = 0, zBroken = false, zRetrying = false;
function syncZepto() {
  zPending++;
  zQueue = zQueue.then(async () => {
    if (!zepto()) return;
    // On a slow phone Zepto can take a moment to show its fields: try three times, about
    // four seconds in all, before telling the customer their names cannot be added
    for (let i = 0; i < 3; i++) {
      if (i) { zRetrying = true; holdButton(); await wait(1200); }
      await zApply(); await wait(60);
      zBroken = !zLanded();
      if (!zBroken) break;
    }
    zRetrying = false;
    if (zBroken) console.warn('wedding customiser: names or logo did not reach Zepto');
    showPrice(); holdButton();
  }).catch((e) => { zBroken = true; zRetrying = false; holdButton(); console.warn('wedding customiser: Zepto sync', e); }).finally(() => { zPending--; });
  return zQueue;
}
// A click straight after typing waits for Zepto's fields to catch up, then goes through
cta.addEventListener('click', (e) => {
  if (!zPending) return;
  e.preventDefault(); e.stopImmediatePropagation();
  zQueue.then(() => cta.click());
}, true);
// Name prices as Zepto labels them, e.g. "First Embroidery Line (RM188)"
function linePrice(re) {
  const f = zField(re, 'input');
  const m = f?.name.match(/\(RM\s*([\d,.]+)\)/);
  return m ? Math.round(parseFloat(m[1].replace(/,/g, '')) * 100) : null;
}
function namesCost() {
  if (zepto() && typeof window.PPLR_CAlCULATE_PRICE === 'function') return Math.round((window.PPLR_CAlCULATE_PRICE() || 0) * 100);
  return 0;
}
function showPrice() {
  const v = current(), extra = lines().length || wantsLogo() ? namesCost() : 0;
  $('[data-wc-price]').textContent = money(v.price + extra);
  const n = $('[data-wc-names-cost]');
  n.hidden = !extra; n.textContent = extra ? `${CFG.text.namesIncluded} ${money(extra)}` : '';
}
// Getha prices embroidery per line: one line, or one line plus a second
function showNamePrices() {
  const p = linePrice(ZEPTO.first) ?? CFG.namePrice, p2 = linePrice(ZEPTO.second) ?? CFG.secondLinePrice;
  const el = $('[data-wc-name-prices]');
  if (p) { el.textContent = `One line of embroidery costs ${money(p)}.` + (p2 ? ` Two lines cost ${money(p + p2)}.` : ''); el.hidden = false; }
  // The same prices beside each field: the names line, then what the second line adds
  $('[data-wc-line-price="name"]').textContent = p ? `+${money(p)}` : '';
  $('[data-wc-line-price="line2"]').textContent = p2 ? `+${money(p2)}` : '';
  const pl = linePrice(ZEPTO.logo) ?? CFG.logoPrice;
  $('[data-wc-custom] small').textContent = $('[data-wc-line-price="logo"]').textContent = `+${money(pl)}`;
}

// Names and logos are paid through Zepto: without it, or if they did not reach it, hold the
// button rather than send embroidery that is not charged. A custom logo also needs its file.
function holdButton() {
  const v = current(), paid = lines().length > 0 || wantsLogo(), z = !!zepto();
  const noFile = state.custom && !state.logo;
  // Zepto is on the page but has no logo charge: say so at once, no need to wait for retries
  const noCharge = wantsLogo() && z && !zField(ZEPTO.logo, 'input[type=checkbox]');
  const blocked = noFile || noCharge || (paid && (!z || zBroken));
  cta.disabled = !v.available || blocked;
  cta.textContent = !v.available ? CFG.text.soldOut : !blocked ? CFG.text.add : noFile ? CFG.text.logoNeeded
    : noCharge ? CFG.text.logoError : zRetrying || (!z && !zGaveUp) ? CFG.text.wait : wantsLogo() ? CFG.text.logoError : CFG.text.namesError;
}
// Zepto attaches within a few seconds on products it prices. On a product it does not price,
// stop saying "loading" and say names cannot be added
let zGaveUp = false;
setTimeout(() => { if (!zepto()) { zGaveUp = true; holdButton(); } }, 8000);

// Only names that will be stitched travel with the order. With Zepto on the page they travel
// in Zepto's fields; our own fields are only a fallback if Zepto is missing.
function syncForm() {
  const v = current();
  idInput.value = v.id;
  const ls = lines(), off = !!zepto() || !ls.length;
  props.name.value = ls[0]?.text || ''; props.line2.value = ls[1]?.text || '';
  props.place.value = PLACE_LABEL[state.place] || ''; props.colour.value = state.colour.label;
  for (const k in props) props[k].disabled = off;
  // The logo file travels with the order as a Shopify upload; Zepto only carries the charge
  $('[data-wc-design]').disabled = $('[data-wc-logo]').disabled = !state.custom;
  holdButton();
  showPrice();
  syncZepto();
  const url = new URL(location.href); url.searchParams.set('variant', v.id);
  history.replaceState(history.state, '', url);
}
// When Zepto attaches (it loads after this script), copy the names across
new MutationObserver(() => { if (zepto() && !form.dataset.wcZepto) { form.dataset.wcZepto = '1'; showNamePrices(); syncForm(); } })
  .observe(form, { childList: true, subtree: true });

// ---------- UI ----------
const LOGO_MAX = 2 * 1024 * 1024;
function showFields() {
  // A custom logo fills the whole panel, so the names step steps aside
  $('[data-wc-names-step]').hidden = state.custom; $('[data-wc-custom-note]').hidden = !state.custom;
  $('[data-wc-names]').hidden = state.place === 'none'; $('[data-wc-line2]').hidden = state.place !== 'both';
  // One line can hold the names or the date; two lines split them
  $('[data-wc-name-label]').textContent = state.place === 'both' ? CFG.text.nameBoth : CFG.text.nameOne;
}
// Names: letters, numbers, spaces and & . , ' - / only (what the panel embroidery shows)
const clean = (s) => s.replace(/[^A-Za-z0-9 &.,'\/-]/g, '').slice(0, MAX);
function buildUI() {
  const sizes = $('.wc-sizes');
  for (const s of SIZES) {
    const b = document.createElement('button'); b.type = 'button'; b.className = 'wc-size' + (s === state.size ? ' wc-on' : '');
    const m = s.match(/^(.*?)\s*\((.*)\)$/) || [, s, ''];
    b.innerHTML = `<span></span><small></small>`; b.firstChild.textContent = m[1]; b.lastChild.textContent = m[2];
    b.addEventListener('click', () => {
      state.size = s; sizes.querySelectorAll('button').forEach((e) => e.classList.toggle('wc-on', e === b)); syncForm();
    });
    sizes.append(b);
  }
  for (const s of SYMBOLS) {
    const b = document.createElement('button'); b.type = 'button'; b.className = 'wc-sym' + (s.id === state.sym ? ' wc-on' : ''); b.title = s.label;
    const im = document.createElement('img'); im.src = A.vec[s.id]; im.alt = s.label; im.loading = 'lazy'; im.width = 200; im.height = 240;
    const span = document.createElement('span'); span.textContent = s.label;
    b.append(im, span);
    b.addEventListener('click', () => {
      state.sym = s.id; state.custom = false; $('[data-wc-upload]').hidden = true;
      root.querySelectorAll('.wc-sym').forEach((e) => e.classList.toggle('wc-on', e === b));
      showFields(); drawDecal(); flyTo('close'); syncForm();
    });
    $(`[data-wc-group="${s.group}"]`).append(b);
  }
  root.querySelectorAll('[data-wc-group]').forEach((g) => { g.closest('.wc-series').hidden = !g.children.length; });
  // Custom embroidery: the customer's own logo across the whole panel
  const customBtn = $('[data-wc-custom]'), upload = $('[data-wc-upload]'), logoIn = $('[data-wc-logo]'), logoMsg = $('[data-wc-logo-msg]');
  customBtn.addEventListener('click', () => {
    state.custom = true; upload.hidden = false;
    root.querySelectorAll('.wc-sym').forEach((e) => e.classList.toggle('wc-on', e === customBtn));
    showFields(); drawDecal(); flyTo('close'); syncForm();
  });
  const logoFail = (msg) => { logoMsg.textContent = msg; logoMsg.classList.add('wc-error'); logoIn.value = ''; state.logo = null; drawDecal(); syncForm(); };
  logoIn.addEventListener('change', async () => {
    const f = logoIn.files[0];
    logoMsg.classList.remove('wc-error');
    if (!f) return logoFail(CFG.text.logoHint);
    const ext = (f.name.match(/\.(png|jpe?g|svg|pdf)$/i) || [])[1]?.toLowerCase();
    if (!ext) return logoFail('Please choose a PNG, JPG, SVG or PDF file.');
    if (f.size > LOGO_MAX) return logoFail('Please choose a file under 2 MB.');
    if (ext === 'pdf') state.logo = { pdf: true };
    else {
      try { state.logo = await img(URL.createObjectURL(f)); } catch (e) { return logoFail('This file could not be opened. Please try another.'); }
      customBtn.querySelector('img').src = state.logo.src;
    }
    state.logo.fileName = f.name;
    logoMsg.textContent = `${f.name} added. ${CFG.text.logoHint}`;
    drawDecal(); flyTo('close'); syncForm();
  });
  root.querySelectorAll('[data-wc-thumb]').forEach((b) => b.addEventListener('click', () => showStage(b.dataset.wcThumb ? b : null)));
  // Visualise your design: the proof canvas keeps the panel's proportions
  const proof = $('[data-wc-proof]'), [pw, ph] = BAND.decalPx;
  proof.width = 360; proof.height = Math.round(360 * ph / pw);
  $('[data-wc-proof-3d]').addEventListener('click', () => {
    flyTo('close');
    if (matchMedia('(max-width: 989px)').matches) stage.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
  root.querySelectorAll('.wc-place button').forEach((b) => b.addEventListener('click', () => {
    state.place = b.dataset.place;
    root.querySelectorAll('.wc-place button').forEach((e) => e.classList.toggle('wc-on', e === b));
    showFields(); drawDecal(); flyTo('close'); syncForm();
  }));
  showFields();
  // Both lines take the same 18 characters
  for (const key of ['name', 'line2']) {
    const el = $(`[data-wc-input="${key}"]`), count = $(`[data-wc-count="${key}"]`);
    el.addEventListener('input', () => {
      const v = clean(el.value); if (v !== el.value) el.value = v;
      state[key] = v; count.textContent = `${v.length}/${MAX}`; drawDecal(); flyTo('close'); syncForm();
    });
  }
  const colours = $('[data-wc-colours]'), colourName = $('[data-wc-colour-name]');
  for (const c of COLOURS) {
    const b = document.createElement('button'); b.type = 'button'; b.className = 'wc-colour' + (c === state.colour ? ' wc-on' : '');
    b.style.background = c.hex; b.title = c.label; b.setAttribute('aria-label', c.label);
    b.addEventListener('click', () => {
      state.colour = c; colours.querySelectorAll('button').forEach((e) => e.classList.toggle('wc-on', e === b));
      colourName.textContent = c.label; drawDecal(); flyTo('close'); syncForm();
    });
    colours.append(b);
  }
  colourName.textContent = state.colour.label;
  root.querySelectorAll('.wc-views button').forEach((b) => b.addEventListener('click', () => flyTo(b.dataset.view)));
  root.classList.add('wc-ready');
}

try { await document.fonts.load('400 100px Damion', 'Jonathan & Jessica'); } catch (e) { /* falls back to cursive */ }
resize();
buildMattress();
buildUI();
if (zepto()) form.dataset.wcZepto = '1';
showNamePrices();
syncForm();
const v0 = viewFor('full'); camera.position.copy(v0.pos); controls.target.copy(v0.tgt);
$('.wc-loading')?.remove();
tick();
(window.requestIdleCallback || setTimeout)(() => SYMBOLS.forEach((s) => symbol(s.id)));
root.__wc = { camera, controls, state, current };
